import React, { useState, useEffect } from 'react'
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Grid,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  Chip
} from '@mui/material'
import {
  Add,
  List as ListIcon,
  ArrowBack,
  Home,
  Lock,
  Dashboard,
  Assessment,
  Schedule
} from '@mui/icons-material'
import { useParams, useNavigate } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import ClosureForm from '../components/mrd/Closure/ClosureForm'
import ClosureList from '../components/mrd/Closure/ClosureList'
import ClosureDashboard from '../components/mrd/Closure/ClosureDashboard'
import closureService from '../services/closureService'

export const ClosurePage = () => {
  const { fileId } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  
  const [tabValue, setTabValue] = useState(0)
  const [showForm, setShowForm] = useState(!!fileId)
  const [dashboardData, setDashboardData] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!fileId) {
      loadDashboardData()
    }
  }, [fileId])

  const loadDashboardData = async () => {
    setLoading(true)
    try {
      const response = await closureService.getClosureDashboard()
      if (response.data.success) {
        setDashboardData(response.data.data)
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleClosureSuccess = (closure) => {
    enqueueSnackbar('File closed successfully', { variant: 'success' })
    setShowForm(false)
    if (fileId) {
      navigate('/hospital/mrd/mrd-files')
    } else {
      loadDashboardData() // Refresh dashboard
    }
  }

  const renderTabContent = () => {
    switch (tabValue) {
      case 0:
        return <ClosureDashboard data={dashboardData} loading={loading} />
      case 1:
        return <ClosureList onRefresh={loadDashboardData} />
      default:
        return null
    }
  }

  // If fileId is provided, show the closure form directly
  if (fileId) {
    return (
      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            color="inherit"
            onClick={() => navigate('/hospital/mrd/dashboard')}
          >
            <Home sx={{ mr: 0.5 }} fontSize="small" />
            Dashboard
          </Link>
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            color="inherit"
            onClick={() => navigate('/hospital/mrd/mrd-files')}
          >
            <ListIcon sx={{ mr: 0.5 }} fontSize="small" />
            MRD Files
          </Link>
          <Typography
            sx={{ display: 'flex', alignItems: 'center' }}
            color="text.primary"
          >
            <Lock sx={{ mr: 0.5 }} fontSize="small" />
            File Closure
          </Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              Close MRD File
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Complete the closure process for this MRD file
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/hospital/mrd/mrd-files')}
            size="large"
          >
            Back to Files
          </Button>
        </Box>

        {/* Closure Form */}
        <Paper sx={{ p: 4, borderRadius: 2 }}>
          <ClosureForm
            fileId={fileId}
            onSuccess={handleClosureSuccess}
            onCancel={() => navigate('/hospital/mrd/mrd-files')}
          />
        </Paper>
      </Container>
    )
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          underline="hover"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          color="inherit"
          onClick={() => navigate('/hospital/mrd/dashboard')}
        >
          <Home sx={{ mr: 0.5 }} fontSize="small" />
          Dashboard
        </Link>
        <Typography
          sx={{ display: 'flex', alignItems: 'center' }}
          color="text.primary"
        >
          <Lock sx={{ mr: 0.5 }} fontSize="small" />
          MRD Closure
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            MRD File Closure Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Monitor, manage, and track MRD file closures with comprehensive analytics
          </Typography>
        </Box>
      </Box>

      {/* Quick Stats */}
      {dashboardData && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" color="primary" fontWeight={700}>
                  {dashboardData.summary?.recentClosures || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Recent Closures (30 days)
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" color="success.main" fontWeight={700}>
                  {dashboardData.summary?.readyForClosure || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Ready for Closure
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" color="info.main" fontWeight={700}>
                  {dashboardData.summary?.totalClosures || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Total Closures
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <Typography variant="h3" color="warning.main" fontWeight={700}>
                  {dashboardData.summary?.lockedClosures || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary" fontWeight={500}>
                  Locked Closures
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs 
          value={tabValue} 
          onChange={(e, val) => setTabValue(val)}
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              minHeight: 72,
              fontSize: '1rem',
              fontWeight: 600
            }
          }}
        >
          <Tab 
            label="Dashboard" 
            icon={<Dashboard />}
            iconPosition="start"
          />
          <Tab 
            label="Closed Files" 
            icon={<ListIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Paper sx={{ borderRadius: 2 }}>
        {renderTabContent()}
      </Paper>
    </Container>
  )
}
